import { AfterViewInit, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Curved Column Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-curved-column-graph',
  standalone: false,
  templateUrl: './curved-column-graph.component.html',
  styleUrl: './curved-column-graph.component.scss'
})
export class CurvedColumnGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() reportData: any = null;
  private root!: am5.Root;
  data: BarChartData[] = [];
  private curvedColumnGraphReportSubscription: any;
  private isCurvedColumnViewInitialized = false;
  private isCreatingChart = false;

  constructor(
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.curvedColumnGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Curved Column Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isCurvedColumnViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isCurvedColumnViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('curved-column-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Curved Column Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isCurvedColumnViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('curved-column-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Curved Column Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('curved-column-chartdiv');
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  createChart(data: BarChartData[]) {
    // Prevent multiple chart creations
    if (this.isCreatingChart) {
      console.log('Curved Column Chart - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    // Check if the chart container exists
    const chartContainer = document.getElementById('curved-column-chartdiv');
    if (!chartContainer) {
      console.error('Curved Column Chart - Chart container not found! Make sure element with id "curved-column-chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Curved Column Chart - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    try {
      console.log('Curved Column Chart - Creating new chart...');
      
      // Create root element
      this.root = am5.Root.new("curved-column-chartdiv");
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'curved-column',
        data: data
      });

      // Set themes
      this.root.setThemes([
        am5themes_Animated.new(this.root)
      ]);

      // Create chart
      let chart = this.root.container.children.push(
        am5xy.XYChart.new(this.root, {
          panX: true,
          panY: true,
          wheelX: "panX",
          wheelY: "zoomX",
          paddingLeft: 5,
          paddingRight: 5,
          layout: this.root.verticalLayout
        })
      );

      // Add cursor
      let cursor = chart.set("cursor", am5xy.XYCursor.new(this.root, {}));
      cursor.lineY.set("visible", false);

      // Get category and value fields
      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      // Create axes
      let xRenderer = am5xy.AxisRendererX.new(this.root, { 
        minGridDistance: 60,
        minorGridEnabled: true
      });

      let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(this.root, {
          maxDeviation: 0.3,
          categoryField: categoryField,
          renderer: xRenderer,
          //tooltip: am5.Tooltip.new(this.root, {})
        })
      );

      xRenderer.grid.template.setAll({
        location: 1
      });

      // Configure labels to prevent overlapping
      xRenderer.labels.template.setAll({
        rotation: -45,
        paddingTop: 10,
        paddingBottom: 10,
        textAlign: "center",
      });
      xRenderer.labels.template.set("maxWidth", 160);
      xRenderer.labels.template.set("oversizedBehavior", "truncate");
      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(this.root, {
          maxDeviation: 0.3,
          renderer: am5xy.AxisRendererY.new(this.root, {
            strokeOpacity: 0.1
          })
        })
      );
     
      // Set data for category axis
      xAxis.data.setAll(data);

      // Create series for each value field
      valueFields.forEach((valueField) => {
        let series = chart.series.push(
          am5xy.ColumnSeries.new(this.root, {
            name: valueField,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: valueField,
            sequencedInterpolation: true,
            categoryXField: categoryField
          })
        );

        // Configure columns with curved appearance
        series.columns.template.setAll({
          width: am5.percent(120),
          fillOpacity: 0.9,
          strokeOpacity: 0,
          tooltipText: "{name}: {valueY}",   // 👈 This shows tooltip
        });

        series.columns.template.adapters.add("fill", (fill, target) => {
          return chart.get("colors")?.getIndex(series.columns.indexOf(target));
        });

        series.columns.template.adapters.add("stroke", (stroke, target) => {
          return chart.get("colors")?.getIndex(series.columns.indexOf(target));
        });
        

        // Add curved drawing function using bezier curves
        series.columns.template.set("draw", function(display, target) {
          let w = target.getPrivate("width", 0);
          let h = target.getPrivate("height", 0);
          display.moveTo(0, h);
          display.bezierCurveTo(w / 4, h, w / 4, 0, w / 2, 0);
          display.bezierCurveTo(w - w / 4, 0, w - w / 4, h, w, h);
        });

        // Set data for series
        series.data.setAll(data);
      });

      // Add legend at the bottom of the chart
      let legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      this._baseChartService.setupLegendAnimation(legend);

      legend.setAll({
        maxHeight: 40,
        marginTop: 15,
        marginBottom: 0
      });

      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      legend.data.setAll(chart.series.values);

      // Make stuff animate on load
      chart.series.values.forEach(series => {
        series.appear(1000);
      });
      chart.appear(1000, 100);

      console.log('Curved Column Chart - Chart created successfully');
      this.isCreatingChart = false;

    } catch (error) {
      console.error('Error creating curved column chart:', error);
      this.isCreatingChart = false;
    }
  }

  private updateChart(data: BarChartData[]) {
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Curved Column Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('curved-column-chartdiv');
    if (!chartContainer) {
      console.log('Curved Column Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Dispose existing chart first
    if (this.root) {
      console.log('Curved Column Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.curvedColumnGraphReportSubscription) {
      this.curvedColumnGraphReportSubscription.unsubscribe();
    }
  }
}
